import React, { useState } from 'react';
import { ClipboardList, Trash2, GripVertical, Pencil, X } from 'lucide-react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';
import QuestionDisplay from './QuestionDisplay';

function SurveyPreview({ title, questions, setQuestions, onUpdateTitle }) {
  const [answers, setAnswers] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleInputChange = (questionIndex, value, type) => {
    if (type === 'checkbox-list') {
      // For checkboxes, maintain an array of selected values
      setAnswers(prev => ({
        ...prev,
        [questionIndex]: {
          ...prev[questionIndex],
          [value]: !((prev[questionIndex] || {})[value])
        }
      }));
    } else {
      // For other input types, store the value directly
      setAnswers(prev => ({
        ...prev,
        [questionIndex]: value
      }));
    }
  };

  const handleDelete = (index) => {
    const newQuestions = [...questions];
    newQuestions.splice(index, 1);
    setQuestions(newQuestions);
    setShowDeleteConfirm(null);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newQuestions = [...questions];
    const [reorderedItem] = newQuestions.splice(result.source.index, 1);
    newQuestions.splice(result.destination.index, 0, reorderedItem);
    
    // Reorder answers to match new question positions
    const newAnswers = {};
    newQuestions.forEach((_, index) => {
      if (result.source.index === index) {
        newAnswers[index] = answers[result.destination.index];
      } else if (result.destination.index === index) {
        newAnswers[index] = answers[result.source.index];
      } else {
        newAnswers[index] = answers[index];
      }
    });
    
    setAnswers(newAnswers);
    setQuestions(newQuestions);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 min-h-full">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="w-6 h-6" />
        <h2 className="text-xl font-semibold">Survey Preview</h2>
      </div>

      {questions.length === 0 ? (
        <p className="text-gray-500 italic py-4">No questions have been added yet.</p>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <StrictModeDroppable droppableId="questions">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-6">
                {questions.map((question, index) => (
                  <Draggable key={index} draggableId={`question-${index}`} index={index}>
                    {(provided) => (
                      <QuestionDisplay
                        question={question}
                        index={index}
                        answers={answers}
                        handleInputChange={handleInputChange}
                        isAdmin={true}
                        onDeleteClick={setShowDeleteConfirm}
                        dragHandleProps={provided.dragHandleProps}
                        provided={provided}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <h3 className="text-lg font-medium mb-4">Are you sure you want to delete this question?</h3>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SurveyPreview;